import semver from "semver";

/**
 * Eigen runtime <-> app version-compatibility gate (A2 / #72).
 *
 * An app may declare `app.dependencies.eigen` (a semver range). The runtime renders
 * the app only when its own VERSION satisfies that range; otherwise it fails CLEANLY
 * with a human message instead of rendering against an incompatible runtime and
 * breaking subtly later.
 *
 * Extracted to a PURE, framework-free helper so it is shared by BOTH app loaders
 * (the v1 route `routes/app/index.tsx` and the experimental v2 route
 * `routes/_/app/index.tsx`) and is unit-gateable without a running runtime. The error
 * message string is preserved EXACTLY so the v1 refactor is behavior-identical.
 */

export interface EigenVersionCompat {
  /** true when the app declares no `eigen` dep, or the runtime satisfies the range. */
  compatible: boolean;
  /** the declared range, when present. */
  required?: string;
  /** the runtime version checked against. */
  runtime: string;
}

/**
 * Non-throwing check. Returns `{ compatible: true }` when the app declares no `eigen`
 * dependency (the common case). Pure + deterministic -> unit-testable.
 */
export function checkEigenVersionCompat(
  dependencies: Record<string, string> | undefined | null,
  runtimeVersion: string
): EigenVersionCompat {
  const required =
    dependencies && "eigen" in dependencies ? dependencies.eigen : undefined;

  if (!required) return { compatible: true, runtime: runtimeVersion };

  return {
    compatible: semver.satisfies(runtimeVersion, required),
    required,
    runtime: runtimeVersion,
  };
}

/**
 * Throwing variant used by the route loaders. Throws the SAME message the v1 loader
 * has always thrown, so swapping the inline check for this call is behavior-identical.
 */
export function assertEigenVersionCompat(
  dependencies: Record<string, string> | undefined | null,
  runtimeVersion: string
): void {
  const result = checkEigenVersionCompat(dependencies, runtimeVersion);
  if (!result.compatible) {
    throw new Error(
      `This app requires Eigen version ${result.required}. You are using version ${result.runtime}.`
    );
  }
}
